import React, { useContext, useEffect } from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';
import { withPrismicPreview } from 'gatsby-plugin-prismic-previews';
import { PrismicRichText } from '@prismicio/react';

import { GlobalContext } from '../components/GlobalContext';
import bp from '../assets/js/breakpoints';

import Seo from '../components/Seo';
import RecentStoriesSlider from '../components/RecentStoriesSlider';
import SliceZone from '../components/SliceZone';
import StoryHero from '../components/StoryHero';
import ShareDesktop from '../components/ShareDesktop';

const YouTube = ({ id }) => (
  <YouTubeWrapper>
    <YouTubeEmbed>
      <iframe
        width="560"
        height="315"
        src={`https://www.youtube.com/embed/${id}`}
        title="YouTube video player"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowFullScreen
      ></iframe>
    </YouTubeEmbed>
  </YouTubeWrapper>
);

const PrismicStory = ({ data }) => {
  const document = data.prismicStory;
  const { full_firstpub_date, url } = document;
  const {
    title,
    description,
    story_color,
    full_custompub_date,
    youtube_id,
    body,
  } = document.data;

  const { setStoryHeaderColor, setPageBgColor } = useContext(GlobalContext);

  useEffect(() => {
    setPageBgColor('var(--color-snow)');
    setStoryHeaderColor(story_color);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <StoryHero data={document.data} url={url} />

      <Intro>
        <IntroText>
          <p className="intro-date">
            {full_custompub_date ? full_custompub_date : full_firstpub_date}
          </p>

          <IntroDescription>
            <PrismicRichText field={description.richText} />
          </IntroDescription>
        </IntroText>

        {youtube_id && <YouTube id={youtube_id} />}
      </Intro>

      {body.length > 0 && (
        <Body>
          <SliceZone slices={body} />

          {youtube_id && (
            <WatchVideo>
              <h2>Watch Video</h2>

              <YouTube id={youtube_id} />
            </WatchVideo>
          )}

          <ShareDesktop url={url} title={title} />
        </Body>
      )}

      <RecentStoriesSlider variant="footer" />
    </>
  );
};

/**
 * Head export to define metadata for the page
 *
 * See: https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-head/
 */
export const Head = ({ data }) => {
  const document = data.prismicStory;

  // If there's no provided meta description/image, use the page
  //  description/image as a fallback
  const description =
    document.data.meta_description || document.data.description.text;
  const image =
    document.data.meta_image.url || document.data.featured_image.url;

  return (
    <Seo
      title={data.prismicStory.data.title}
      themeColor={data.prismicStory.data.story_color}
      description={description}
      image={image}
    />
  );
};

export default withPrismicPreview(PrismicStory);

/**
 * Styled Components
 *
 */
const YouTubeWrapper = styled.div`
  @media (${bp.min.md}) {
    display: flex;
    align-items: center;
    padding-left: var(--space-m);
    margin-left: var(--space-m);
    border-left: 0.1rem solid var(--color-navy);
  }
`;

const YouTubeEmbed = styled.div`
  position: relative;
  width: 100%;
  height: 0;
  padding-top: calc(9 / 16 * 100%);

  iframe {
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
  }
`;

const Intro = styled.div`
  position: relative;
  border-bottom: 0.1rem solid ${props => props.contrastingColor};
  padding: var(--space-m) 0 4.8rem;
  margin-bottom: var(--space-xl);

  @media (${bp.min.xs_sm}) {
    padding: var(--space-xl) 0 4.8rem;
  }

  @media (${bp.min.sm}) {
    padding: 4.8rem 0;
    margin-bottom: 4.8rem;
  }

  @media (${bp.min.md}) {
    display: grid;
    grid-template-columns: 65% 35%;
  }

  &:after {
    content: '';
    width: 100%;
    border-bottom: 0.1rem solid ${props => props.contrastingColor};
    position: absolute;
    bottom: 0.4rem;
  }

  ${YouTubeWrapper} {
    display: none;

    @media (${bp.min.md}) {
      display: flex;
    }
  }
`;

const IntroText = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  @media (${bp.min.md}) {
    max-width: 96rem;
  }

  .intro-date {
    font-size: 2.2rem;
    font-weight: var(--font-weight-bold);
    letter-spacing: 0.04rem;
    color: var(--color-navy);
    text-transform: uppercase;
    margin-bottom: var(--space-m);
    opacity: 0.75;

    @media (${bp.min.xs_sm}) {
      opacity: 0.5;
      font-size: 1.2rem;
      margin-bottom: var(--space-l);
    }
  }
`;

const IntroDescription = styled.div`
  font-size: clamp(2.8rem, 5vw, 4.2rem);
  line-height: 1.15;
  font-weight: var(--font-weight-bold);
  letter-spacing: -0.05em;
`;

const Body = styled.article`
  --full: minmax(0, 1fr);
  --content: min(65ch, 100%);
  --popout: minmax(0, 1.5fr);

  display: grid;
  grid-template-columns:
    [full-start] var(--full)
    [popout-start] var(--popout)
    [content-start] var(--content) [content-end]
    var(--popout) [popout-end]
    var(--full) [full-end];
  padding-bottom: var(--space-xxl);

  @media (${bp.min.xs_sm}) {
    border-bottom: 0.1rem solid var(--color-navy);
  }

  .text-slice + .text-slice {
    margin-top: var(--space-m);
  }

  .image-slice + .text-slice {
    margin-top: 0;
  }

  .image-slice + .image-slice {
    margin-top: 0;
  }
`;

const WatchVideo = styled.section`
  grid-column: content;
  margin-top: 9.6rem;

  h2 {
    font-size: 4.4rem;
    line-height: 1;
    text-transform: uppercase;
    letter-spacing: -0.1rem;
    text-align: center;
    padding-bottom: var(--space-s);
    border-bottom: 0.5rem solid var(--color-navy);
    margin-bottom: var(--space-l);
  }

  @media (${bp.min.md}) {
    display: none;
  }
`;

/**
 * GraphQL
 *
 */
export const query = graphql`
  query ($id: String!) {
    prismicStory(id: { eq: $id }) {
      _previewable
      url
      full_firstpub_date: first_publication_date(formatString: "MMMM DD, YYYY")
      data {
        title
        description {
          richText
          text
        }
        story_number
        story_color
        full_custompub_date: publish_date(formatString: "MMMM DD, YYYY")
        spotify_id
        youtube_id
        featured_image {
          alt
          gatsbyImageData(width: 800)
          url
        }
        meta_description
        meta_image {
          url
        }
        body {
          ... on PrismicStoryDataBodyText {
            slice_type
            primary {
              text {
                richText
              }
            }
          }
          ... on PrismicStoryDataBodyLargeImage {
            slice_type
            primary {
              image {
                alt
                gatsbyImageData(width: 1200)
              }
            }
          }
          ... on PrismicStoryDataBodyImage2Up {
            slice_type
            primary {
              image_1 {
                alt
                gatsbyImageData(width: 800)
              }
              image_2 {
                alt
                gatsbyImageData(width: 800)
              }
            }
          }
          ... on PrismicStoryDataBodyImage3Up {
            slice_type
            primary {
              image_1 {
                alt
                gatsbyImageData(width: 800)
              }
              image_2 {
                alt
                gatsbyImageData(width: 800)
              }
              image_3 {
                alt
                gatsbyImageData(width: 800)
              }
            }
          }
          ... on PrismicStoryDataBodyQuote {
            slice_type
            primary {
              quote {
                richText
              }
              attribution
            }
          }
          ... on PrismicStoryDataBodyYoutube {
            slice_type
            primary {
              youtube_id
              embed_width
            }
          }
          ... on PrismicStoryDataBodyEmbed {
            slice_type
            primary {
              embed {
                text
              }
              embed_width
            }
          }
        }
      }
    }
  }
`;
